import React, { useState, useEffect } from 'react';
import WujieReact from 'wujie-react';
import { observer } from 'mobx-react';
import Cookies from 'js-cookie';
import { useLocation, useOutletContext } from 'react-router-dom';
import { toJS } from 'mobx';
import { Loading, ErrorPage } from '@/components';
import { PageCacheStore, AccountStore } from '@/stores';
import { ApplicationInfo } from '@/const/application';
import { ApplicationUrlMap } from '@/const/serviceEnv';
import { useTabNavigate } from '@/hooks/useTabNavigate';
import { useWujieProps } from '@/hooks/useWujie';
import theme from '@/theme';

import type { MenuDataItem } from '@/const/route.config';

import './index.less';

const { systemId } = ApplicationInfo;

const { destroyApp } = WujieReact;

interface WujieReactItem {
  path: string;
  pathname: string;
  singleTab?: boolean;
  WujieReactDom: React.ReactElement;
}

interface OutletProps {
  currentMenu: MenuDataItem;
  matchMenu: MenuDataItem[];
  flatMenus: Record<string, MenuDataItem> | null;
}

const eventHandlers = new WeakMap();

const wujieRootCssVars = (() => {
  const res = Object.entries({
    '--primary-color': theme?.token?.colorPrimary,
    '--primary-color-hover': theme?.token?.colorPrimaryHover,
    '--primary-color-active': theme?.token?.colorPrimaryActive,
    '--primary-bg-color': theme?.token?.colorPrimaryBg,
    '--primary-bg-color-hover': theme?.token?.colorPrimaryBgHover,
  }).reduce((res, [key, value]) => {
    if (value) {
      return `${res}${key}:${value};`;
    }
    return res;
  }, '');

  return `:root{${res}}`;
})();

const wujiePlugins = [
  {
    jsBeforeLoaders: [
      {
        content: `
        window.axios = window.parent.axios;
        window.React = window.parent.React;
        window.ReactDOM = window.parent.ReactDOM;
        window.classnames = window.parent.classnames;
        window.Selection = window.parent.Selection;
        window.global = global || globalThis || window;
      `,
      },
    ],
    cssBeforeLoaders: [
      {
        content: `
        ${wujieRootCssVars}
        body{background:transparent !important}
        #root{display:flex;flex-direction:column;}
        .fx-router-loading{min-height:calc(100vh - 124px) !important;}
        .fx-router-loading .fx-spin-dot i{background-color:${theme?.token?.colorPrimary} !important;}
      `,
      },
    ],
    windowAddEventListenerHook(
      iframeWindow: typeof Window,
      type: string,
      handler: Function,
      options: Object,
    ) {
      if (['mousedown'].includes(type)) {
        const _handler = function (e: Event) {
          const [target] = e.composedPath();
          Object.defineProperty(e, 'target', {
            value: target,
            writable: true,
            enumerable: true,
            configurable: true,
          });
        };
        eventHandlers.set(handler, _handler);
        window.addEventListener(type, _handler, options);
      }
    },
    windowRemoveEventListenerHook(
      iframeWindow: typeof Window,
      type: string,
      handler: Function,
      options: Object,
    ) {
      if (['mousedown'].includes(type)) {
        const _handler = eventHandlers.get(handler);
        eventHandlers.delete(handler);
        window.removeEventListener(type, _handler, options);
      }
    },
  },
];

const MicroApp: React.FC = () => {
  const [uninitialized, setUninitialized] = useState(true);
  const navigate = useTabNavigate();
  const location = useLocation();

  const { currentMenu, flatMenus } = useOutletContext<OutletProps>();
  const [wujieReact, setWujieReact] = useState<WujieReactItem>();

  const [errors, setErrors] = useState<{ [key: string]: boolean }>({});

  const { singleTab, wujieMode } = JSON.parse(decodeURIComponent(currentMenu?.props || '{}'));
  const currentPath = `${location.pathname}${singleTab ? '' : location.search}`;

  // 透传给子应用的props
  const wjProps = useWujieProps(
    {
      theme,
      systemId,
      wujieMode: wujieMode || 'rebuild',
      systemIds: [systemId],
      getUserInfo() {
        return toJS(AccountStore.userInfo);
      },
      ApplicationUrlMap,
      // 跳转页面
      navigate: (urlOrDelta: string | number, options?: { target?: string; replace?: string }) => {
        if (typeof urlOrDelta === 'string') {
          if (options?.target === '_blank') {
            window.open(urlOrDelta, options.target);
            return;
          }

          navigate(urlOrDelta);
          return;
        }

        if (urlOrDelta === 0) {
          PageCacheStore.refreshPage(`${location.pathname}${location.search}`).then((path) => {
            path && navigate('/redirectPage', { state: { path }, replace: true });
          });
          return;
        }

        navigate(urlOrDelta);
      },
      // 重定向到某个页面并刷新（不传即为当前页）
      refreshPage: (path?: string) => {
        PageCacheStore.refreshPage(path || `${location.pathname}${location.search}`).then(
          (path) => {
            path && navigate('/redirectPage', { state: { path }, replace: true });
          },
        );
      },

      // 关闭当前页面
      closeSinglePage: (path?: string) =>
        PageCacheStore.closePageTab(path || `${location.pathname}${location.search}`),

      // 关闭所有页面
      closeAllPage: () => PageCacheStore.closeAllTabs(),

      // 退出登录
      logoutAndGoToLogin: (url?: string) => {
        Promise.allSettled([AccountStore.logout(), AccountStore.logoutother()]).then((res) => {
          if (res.filter((item) => item.status === 'fulfilled').length > 0) {
            [
              'token',
              'liguanjia_member_token',
              'liguanjia_supplier_token',
              'liguanjia_card_token',
              'liguanjia_member_token',
            ].forEach(async (item: string) => {
              await Cookies.remove(item);
            });
            const loginUrl = `/account/login?redirectUrl=${encodeURIComponent(
              window.location.href,
            )}`;
            window.location.replace(url || loginUrl);
          }
        });
      },
      getNewPlusTag() {
        AccountStore.getUserInfo().then((data) => {
          // setUserInfo(data); 这里需要更新一下全局的权益状态
          AccountStore.updateUserInfo((info: any) => ({
            ...info,
            personal: data?.customerType === '21',
            rightsTag: data?.plusFlag,
            companyName: data?.companyName,
            adminFlag: data?.adminFlag,
            plate: '100',
          }));
          AccountStore.getTeamInfo({ company_id: data?.companyId }).then((res) => {
            AccountStore.updateUserInfo((info: any) => ({
              ...info,
              is_open_supplier: res?.data.is_open_supplier,
            }));
          });
        });
      },
    },
    [currentMenu],
  );

  useEffect(() => {
    if (!currentMenu) return;

    setWujieReact(() => {
      const loadUrl = `${location.pathname}${location.search}`.replace(/^\/module/, '');

      const WujieReactDom = (
        <WujieReact
          fiber
          width="100%"
          height="100%"
          url={loadUrl}
          key={loadUrl}
          name={currentPath}
          props={{ ...wjProps }}
          plugins={wujiePlugins}
          loadError={() => {
            setErrors((errors) => ({ ...errors, [currentPath]: true }));
          }}
          replace={(codeText) => codeText.replace(/const global = globalThis;\s*/g, '')}
        />
      );

      const newWujieReact = {
        WujieReactDom,
        path: currentPath,
        pathname: window.location.pathname,
      };

      return newWujieReact;
    });
  }, [currentMenu]);

  useEffect(() => {
    flatMenus && setUninitialized(false);
  }, [flatMenus]);

  // 组件卸载时销毁无界实例
  useEffect(() => {
    return () => {
      if (wujieReact?.path) {
        destroyApp(wujieReact.path);
      }
    };
  }, [wujieReact?.path]);

  // 未初始化时
  if (uninitialized) return <Loading spinning />;

  if (!currentMenu) return <ErrorPage status="noAuth" />;

  return (
    <div className="wujie-container">
      {errors[currentPath] ? <ErrorPage status="error" /> : wujieReact?.WujieReactDom}
    </div>
  );
};
export default observer(MicroApp);
